import { useCallback, useEffect, useState } from "react";
import EducationalHubApi from "../../../api/educationalHubApi";
import { Button } from "../../../components/ui/button";
import {
  EducationalHub,
  TypeEducationalHubFilters,
} from "../../../types/EducationalHub";
import Notify from "../../../utils/toaster";
import CreateHubModal from "./CreateHubModal";
import HubFilters from "./HubFilters";
import HubListTable from "./HubListTable";
import s from "./HubManagement.module.css";

type AppliedHubFilters = Omit<TypeEducationalHubFilters, "page" | "limit">;

const getFallbackCount = (
  page: number,
  rowsPerPage: number,
  rowsLength: number,
  totalAmount?: number,
) => {
  if (typeof totalAmount === "number") return totalAmount;
  return page * rowsPerPage + rowsLength + (rowsLength === rowsPerPage ? 1 : 0);
};

const HubListManagement = () => {
  const token = localStorage.getItem("token");
  const [hubs, setHubs] = useState<EducationalHub[]>([]);
  const [logos, setLogos] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<AppliedHubFilters>({});
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
    total: 0,
  });

  const loadHubs = useCallback(
    async (
      page: number,
      limit: number,
      filters: AppliedHubFilters,
    ) => {
      if (!token) return;
      setIsLoading(true);
      try {
        const response = await EducationalHubApi.getFilteredHubs(token, {
          ...filters,
          page: page + 1,
          limit,
        });

        if (response.status && response.hubs) {
          setHubs(response.hubs);
          setPagination((prev) => ({
            ...prev,
            total: getFallbackCount(
              page,
              limit,
              response.hubs?.length ?? 0,
              response.totalAmount,
            ),
          }));
        } else {
          setHubs([]);
          setPagination((prev) => ({ ...prev, total: 0 }));
          if (response.message) Notify(response.message);
        }
      } catch (error) {
        console.error("Error loading Educational Hubs:", error);
        setHubs([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        Notify("Failed to load Educational Hubs");
      } finally {
        setIsLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    loadHubs(pagination.page, pagination.rowsPerPage, activeFilters);
  }, [activeFilters, loadHubs, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    let isCancelled = false;

    const loadLogos = async () => {
      if (!token || hubs.length === 0) return;
      const entries = await Promise.all(
        hubs.map(async (hub) => {
          if (!hub.logo || logos[hub._id]) return null;
          try {
            const response = await EducationalHubApi.getHubFileSignedUrl(
              token,
              hub.logo,
            );
            if (response.status && response.url) {
              return [hub._id, response.url] as const;
            }
          } catch (error) {
            console.error("Error loading hub logo:", error);
          }
          return null;
        }),
      );

      if (isCancelled) return;
      const nextLogos: Record<string, string> = {};
      entries.forEach((entry) => {
        if (entry) nextLogos[entry[0]] = entry[1];
      });
      if (Object.keys(nextLogos).length > 0) {
        setLogos((prev) => ({ ...prev, ...nextLogos }));
      }
    };

    loadLogos();

    return () => {
      isCancelled = true;
    };
  }, [hubs, logos, token]);

  const handleApplyFilters = (filters: AppliedHubFilters) => {
    setActiveFilters(filters);
    setPagination((prev) => ({ ...prev, page: 0 }));
    setIsFiltersOpen(false);
  };

  const reloadCurrentList = () => {
    loadHubs(pagination.page, pagination.rowsPerPage, activeFilters);
  };

  return (
    <div className={s.listManagement}>
      <div className={s.toolbar}>
        <div>
          <h2 className={s.panelTitle}>List of Hubs</h2>
          <p className={s.panelSubtitle}>
            Browse and create Educational Hubs across the platform.
          </p>
        </div>
        <div className={s.toolbarActions}>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsFiltersOpen(true)}
          >
            Filters
          </Button>
          <Button type="button" onClick={() => setIsCreateModalOpen(true)}>
            Create Hub
          </Button>
        </div>
      </div>

      <HubListTable
        rows={hubs}
        logos={logos}
        page={pagination.page}
        rowsPerPage={pagination.rowsPerPage}
        count={pagination.total}
        loader={isLoading}
        onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
        onRowsPerPageChange={(rowsPerPage) =>
          setPagination((prev) => ({ ...prev, rowsPerPage, page: 0 }))
        }
      />

      <button
        type="button"
        aria-label="Close filters"
        className={`${s.drawerBackdrop} ${isFiltersOpen ? s.drawerBackdropVisible : ""}`}
        onClick={() => setIsFiltersOpen(false)}
      />
      <aside
        className={`${s.filtersDrawer} ${isFiltersOpen ? s.filtersDrawerOpen : ""}`}
        aria-hidden={!isFiltersOpen}
      >
        <div className={s.drawerHeader}>
          <h3 className={s.drawerTitle}>Educational Hub filters</h3>
          <button
            type="button"
            className={s.closeDrawerBtn}
            onClick={() => setIsFiltersOpen(false)}
          >
            Close
          </button>
        </div>
        <div className={s.drawerContent}>
          <HubFilters onApply={handleApplyFilters} />
        </div>
      </aside>

      <CreateHubModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={() => {
          setIsCreateModalOpen(false);
          reloadCurrentList();
        }}
      />
    </div>
  );
};

export default HubListManagement;
